import { Button, Form, Input, Modal, Tooltip, Typography, message } from "antd";
import React, { useState, useCallback, useMemo } from "react";
import {
  rescheduleApplicationSuggestedDates,
  calculateAdditionalCharge,
  chargeAdditionalInfluencerCredits,
} from "services/SauceService";
import { ReactComponent as RescheduleIcon } from "assets/images/reschedule-icon.svg";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  BusinessState,
  getBusinessEscrow,
  getBusinessSubscription,
} from "redux-context/business";
import { InfluencerType } from "enums/InfluencerType";
import PaymentCheckout, {
  FeedbackType as PaymentFeedbackType,
} from "components/PaymentCheckout/PaymentCheckout";
import { CheckoutCharge } from "interfaces/checkout-charge";
import { SubscriptionType } from "enums/SubscriptionType";
import { usePaywall } from "components/paywall/paywallContext";
import { PaywallSource } from "components/paywall/Paywall";

interface InfluencerBooking {
  bookingId: string;
  collabId: string;
  tabStatus: "accepted" | "pending" | "rejected" | "expired";
  // optional fields used for payment logic
  isPremium?: boolean;
  selfManagedCompensationEnabled?: boolean;
  cashCompAmount?: number;
}

const convert24HourToAmPmFormat = (time: string) => {
  if (!time) return time;
  // Extract the hours and minutes from the input string
  const [hour, minute] = time.split(":").map(Number);

  // Determine the period (AM/PM)
  const period = hour < 12 ? "AM" : "PM";

  // Convert hours from 24-hour format to 12-hour format
  const hour12 = hour % 12 || 12;

  // Pad the hour with leading zero if necessary
  const formattedHour = hour12 < 10 ? "0" + hour12 : "" + hour12;

  // Pad the minute with leading zero if necessary
  const formattedMinute = minute < 10 ? "0" + minute : "" + minute;

  // Combine to form the new time string in 12-hour format
  return `${formattedHour}:${formattedMinute} ${period}`;
};

interface RescheduleRequestProps {
  open?: boolean;
  asButton?: boolean;
  influencer: InfluencerBooking;
  escrowEnabled?: boolean;
  callback?: () => void;
}

const FormDateSuggestions: React.FC<{
  form: any;
  loading: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}> = (props) => {
  const { form, loading, onSubmit, onCancel } = props;

  return (
    <Form form={form} onFinish={onSubmit}>
      <Form.List name="preferredDates">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} style={{ display: "flex", gap: "8px" }}>
                <Form.Item
                  {...restField}
                  name={[name, "date"]}
                  rules={[{ required: true, message: "Please select a date" }]}
                >
                  <Input
                    type="date"
                    min={
                      new Date(Date.now() + 86400000)
                        .toISOString()
                        .split("T")[0]
                    }
                  />
                </Form.Item>
                <Form.Item
                  {...restField}
                  name={[name, "hour"]}
                  rules={[{ required: true, message: "Please select a time" }]}
                >
                  <Input type="time" />
                </Form.Item>
                <Button type="link" onClick={() => remove(name)}>
                  Remove
                </Button>
              </div>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block>
                Add Date & Time
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Button type="primary" htmlType="submit" loading={loading}>
          Reschedule Booking
        </Button>
        <Button
          style={{ justifySelf: "flex-end" }}
          onClick={onCancel}
          loading={loading}
        >
          Close
        </Button>
      </div>
    </Form>
  );
};

enum Step {
  SuggestDate = 0,
  Payment = 1,
}

const RescheduleRequest: React.FC<RescheduleRequestProps> = (props) => {
  const { influencer, asButton, open = false, escrowEnabled = false } = props;
  const { bookingId, collabId } = influencer;
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [form] = Form.useForm();
  const { openPaywall } = usePaywall();
  const [loading, setLoading] = useState<boolean>(false);
  const [showReschedule, setShowReschedule] = useState<boolean>(open);
  const [dateSelect, setDateSelect] = useState<boolean>(false);
  const [step, setStep] = useState<Step>(Step.SuggestDate);
  const [checkoutInfo, setCheckoutInfo] = useState<
    CheckoutCharge | undefined
  >();
  const [feedback, setFeedback] = useState<{
    type: PaymentFeedbackType;
    message: string;
    receipt?: string;
  }>();
  const [pendingPreferredDates, setPendingPreferredDates] = useState<Array<{
    date: string;
    hour: string;
  }> | null>(null);

  const { subscription, escrow } = useSelector(
    (state: BusinessState) => state.business,
  );

  const influencerType = influencer?.isPremium
    ? InfluencerType.premium
    : InfluencerType.basic;

  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;
  const spotsInformation = subscription?.spotsUsageByType?.find(
    (spot: any) => spot.type === influencerType,
  );

  const limitReached = (spotsInformation?.amountRemaining || 0) < 1;
  const paymentRequired = useMemo(() => {
    return (
      (limitReached ||
        (influencer?.selfManagedCompensationEnabled &&
          (influencer?.cashCompAmount || 0) > 0)) &&
      escrowEnabled
    );
  }, [limitReached, influencer, escrowEnabled]);

  const calculateCheckout = useCallback(async () => {
    try {
      const response: any = await calculateAdditionalCharge({
        collabId: collabId,
        bookingId: bookingId,
      });
      setCheckoutInfo(response);
    } catch (error) {
      console.error("calculateCheckout error", error);
    }
  }, [collabId, bookingId]);

  const submitReschedule = async (
    preferredDates: Array<{ date: string; hour: string }>,
  ) => {
    setLoading(true);
    try {
      await rescheduleApplicationSuggestedDates({
        bookingId: bookingId,
        collabId: collabId,
        preferredDates: preferredDates.map((d) => ({
          date: d.date,
          hour: d.hour,
        })),
      });
      dispatch(getBusinessSubscription());
      dispatch(getBusinessEscrow());
      message.success("Application rescheduled successfully");
      setLoading(false);
      navigate(".", { replace: true });
      form.resetFields();
      setShowReschedule(false);
      setStep(Step.SuggestDate);
      setPendingPreferredDates(null);
    } catch (error) {
      message.error("Error rescheduling application");
      console.log(error);
      setLoading(false);
    }
  };

  const showRescheduleModal = () => {
    if (freemium) {
      openPaywall({
        source: PaywallSource.BookingActions,
        onSubscribe: () => setShowReschedule(true),
      });
      return;
    }
    setShowReschedule(true);
  };

  const onFinishFormDateSuggestions = async () => {
    const { preferredDates = [] } = form.getFieldsValue();

    const normalized = preferredDates.map(
      (date: { date: string; hour: string }) => ({
        date: date.date,
        hour: convert24HourToAmPmFormat(date.hour),
      }),
    );

    setPendingPreferredDates(normalized);

    if (paymentRequired) {
      setStep(Step.Payment);
      await calculateCheckout();
      return;
    }

    await submitReschedule(normalized);
  };

  const onClickPay = async () => {
    if (!pendingPreferredDates) return;
    setLoading(true);
    const params = {
      bookingId: bookingId,
      collabId: collabId,
    };
    try {
      await chargeAdditionalInfluencerCredits(params);

      await submitReschedule(pendingPreferredDates);
      setFeedback({
        type: PaymentFeedbackType.Success,
        message: "Payment successful",
      });
    } catch (error: any) {
      const { response } = error;
      setFeedback({
        type: PaymentFeedbackType.Error,
        message: `${response?.data?.message}.` || "An error occurred.",
      });
      setLoading(false);
    }
  };

  const title = dateSelect ? "Suggest another date" : "Reschedule";

  return (
    <div>
      {asButton ? (
        <Tooltip title="Reschedule this booking">
          <Button
            className="button-icon"
            icon={
              <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: 12 }} />
            }
            style={{ fontSize: 12 }}
            onClick={showRescheduleModal}
          >
            Reschedule
          </Button>
        </Tooltip>
      ) : (
        <span onClick={showRescheduleModal} style={{ cursor: "pointer" }}>
          <RescheduleIcon /> Reschedule
        </span>
      )}
      <Modal
        open={showReschedule}
        title={title}
        onCancel={() => setShowReschedule(false)}
        footer={null}
        className="book-collab-modal"
      >
        {influencer.tabStatus === "accepted" && (
          <Typography.Title level={5} style={{ color: "red" }}>
            Your current booking will be canceled once the creator confirms a
            new date.
          </Typography.Title>
        )}
        <Typography.Text style={{ color: "#999999" }}>
          Select a few new date and time options for the creator to choose from.
        </Typography.Text>

        {step === Step.SuggestDate && (
          <FormDateSuggestions
            form={form}
            loading={loading}
            onSubmit={onFinishFormDateSuggestions}
            onCancel={() => {
              setDateSelect(false);
              setPendingPreferredDates(null);
              setShowReschedule(false);
            }}
          />
        )}

        {step === Step.Payment && (
          <PaymentCheckout
            feedback={feedback as any}
            checkoutInfo={checkoutInfo}
            influencer={influencer}
            spotsInformation={spotsInformation}
            escrow={escrow}
            loading={loading}
            onClose={() => {
              setStep(Step.SuggestDate);
              setPendingPreferredDates(null);
              setFeedback(undefined);
            }}
            onClickPay={onClickPay}
          />
        )}
      </Modal>
    </div>
  );
};

export default RescheduleRequest;
